// SMART on FHIR standalone patient launch: authorization code + PKCE.
//
// PKCE rather than a client secret, because this runs in the browser and a
// secret shipped to a browser is not a secret. The verifier never leaves this
// device; only its SHA-256 hash travels in the authorize URL.

import { EHR_REDIRECT_URI } from "config/ehr";

/** Survives the redirect to the hospital and back, and nothing beyond it. */
const PENDING_KEY = "ehr.smart.pending";
const SESSION_KEY = "ehr.smart.session";

const base64Url = (bytes) =>
  window.btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const randomString = (byteLength = 32) => {
  const buffer = new Uint8Array(byteLength);
  window.crypto.getRandomValues(buffer);
  return base64Url(buffer);
};

async function challengeFor(verifier) {
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new window.TextEncoder().encode(verifier)
  );
  return base64Url(new Uint8Array(digest));
}

/**
 * Endpoints come from the server rather than from constants: every FHIR server
 * publishes them at this well-known path, so the same code works against any
 * of them and survives a vendor moving a URL.
 */
export async function discoverEndpoints(fhirBaseUrl) {
  const response = await fetch(`${fhirBaseUrl}/.well-known/smart-configuration`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(
      `That hospital's server did not answer (HTTP ${response.status}).`
    );
  }
  const config = await response.json();
  if (!config.authorization_endpoint || !config.token_endpoint) {
    throw new Error("That server does not support signing in from other apps.");
  }
  return {
    authorizeUrl: config.authorization_endpoint,
    tokenUrl: config.token_endpoint,
  };
}

/**
 * Returns the URL to send the patient to. The caller navigates — leaving the
 * redirect to the caller keeps this testable without touching window.location.
 */
export async function buildAuthorizeUrl(provider) {
  if (!provider?.clientId) {
    throw new Error(`No client ID is configured for ${provider?.label || "that hospital"}.`);
  }
  const { authorizeUrl, tokenUrl } = await discoverEndpoints(provider.fhirBaseUrl);
  const verifier = randomString();
  const state = randomString(16);

  window.sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify({
      providerId: provider.id,
      fhirBaseUrl: provider.fhirBaseUrl,
      clientId: provider.clientId,
      tokenUrl,
      verifier,
      state,
    })
  );

  const params = new URLSearchParams({
    response_type: "code",
    client_id: provider.clientId,
    redirect_uri: EHR_REDIRECT_URI,
    scope: provider.scopes.join(" "),
    state,
    aud: provider.fhirBaseUrl,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  return `${authorizeUrl}?${params.toString()}`;
}

/**
 * Completes the handshake after the hospital redirects back.
 *
 * The pending record is cleared whatever happens: a code can only be spent
 * once, and React's development double-render would otherwise replay the
 * exchange and fail on the second attempt.
 */
export async function completeAuthorization(search) {
  const params = new URLSearchParams(search);
  const pendingRaw = window.sessionStorage.getItem(PENDING_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);

  if (params.get("error")) {
    throw new Error(
      params.get("error_description") || "The hospital refused the connection."
    );
  }
  if (!pendingRaw) {
    throw new Error("This sign-in has expired. Please start the connection again.");
  }
  const pending = JSON.parse(pendingRaw);
  const code = params.get("code");
  if (!code) throw new Error("The hospital did not return an authorization code.");
  // Guards against a code from another tab or another site being replayed here.
  if (params.get("state") !== pending.state) {
    throw new Error("This sign-in could not be verified. Please try again.");
  }

  const response = await fetch(pending.tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: EHR_REDIRECT_URI,
      client_id: pending.clientId,
      code_verifier: pending.verifier,
    }).toString(),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.access_token) {
    throw new Error(
      payload.error_description || payload.error || "The hospital declined to issue an access token."
    );
  }

  const session = {
    providerId: pending.providerId,
    fhirBaseUrl: pending.fhirBaseUrl,
    accessToken: payload.access_token,
    // Standalone patient launch returns whose chart the token opens.
    patientId: payload.patient || null,
  };
  // sessionStorage, not localStorage: the token dies with the tab, and this
  // demo never asks for a refresh token.
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function readSession() {
  const raw = window.sessionStorage.getItem(SESSION_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function clearSession() {
  window.sessionStorage.removeItem(SESSION_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
}
